import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { message } from '@surecompanies/core_components'
import {
  completeUpload,
  presignUploadUrl,
} from '../api/plan-override-documents-api'
import type { DocumentResponseDto } from '../api/dto/plan-override-document.dto'
import { uploadToS3 } from '../utils/uploadToS3'
import { DOCUMENTS_QUERY_KEY } from './useOverrideDocuments'
import { HISTORY_QUERY_KEY } from './useOverrideHistory'
import {
  ALLOWED_MIME_TYPES,
  MAX_SIZE_BY_MIME,
  MAX_FILES_PER_OVERRIDE,
} from '../utils/fileValidation'

// Optimistic placeholder shown while uploading
export interface UploadingPlaceholder {
  id: string
  fileName: string
  progress: number
  isPlaceholder: true
}

export type DocumentOrPlaceholder = DocumentResponseDto | UploadingPlaceholder

export function isPlaceholder(
  doc: DocumentOrPlaceholder
): doc is UploadingPlaceholder {
  return (doc as UploadingPlaceholder).isPlaceholder === true
}

interface UseUploadOverrideDocumentOptions {
  ruleId: string
  currentDocumentCount: number
  ruleBase?: string
}

export function useUploadOverrideDocument({
  ruleId,
  currentDocumentCount,
  ruleBase,
}: UseUploadOverrideDocumentOptions) {
  const queryClient = useQueryClient()
  const [placeholders, setPlaceholders] = useState<UploadingPlaceholder[]>([])
  // Tracks in-flight uploads synchronously so concurrent drops can't bypass the limit
  const pendingCountRef = useRef(0)

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_MIME_TYPES.has(file.type)) {
      return `${file.name}: unsupported file type. Allowed: PNG, JPEG, WEBP, PDF, DOC, DOCX, XLSX, CSV.`
    }
    const maxSize = MAX_SIZE_BY_MIME[file.type]
    if (file.size > maxSize) {
      const mb = Math.round(maxSize / (1024 * 1024))
      return `${file.name}: exceeds the ${mb} MB limit for this file type.`
    }
    return null
  }, [])

  const upload = useCallback(
    async (file: File): Promise<void> => {
      const validationError = validateFile(file)
      if (validationError) {
        message.error(validationError)
        return
      }

      if (
        currentDocumentCount + pendingCountRef.current >=
        MAX_FILES_PER_OVERRIDE
      ) {
        message.error(
          `Maximum ${MAX_FILES_PER_OVERRIDE} files allowed per override.`
        )
        return
      }
      pendingCountRef.current++

      // Add optimistic placeholder
      const placeholderId = `placeholder-${Date.now()}-${file.name}`
      setPlaceholders((prev) => [
        ...prev,
        {
          id: placeholderId,
          fileName: file.name,
          progress: 0,
          isPlaceholder: true,
        },
      ])

      try {
        // Step 1: presign
        const {
          documentId,
          presignedUploadUrl,
          s3Key: _s3Key,
        } = await presignUploadUrl(
          ruleId,
          {
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
          },
          ruleBase
        )

        // Step 2: XHR PUT to S3 with progress
        const result = await uploadToS3({
          presignedUrl: presignedUploadUrl,
          file,
          contentType: file.type,
          onProgress: (percent) => {
            setPlaceholders((prev) =>
              prev.map((p) =>
                p.id === placeholderId ? { ...p, progress: percent } : p
              )
            )
          },
        })

        if (!result.success) {
          throw result.error ?? new Error('S3 upload failed')
        }

        // Step 3: complete
        await completeUpload(
          ruleId,
          documentId,
          { sizeBytes: file.size },
          ruleBase
        )

        // Invalidate both documents + history so new doc and new log row appear
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [DOCUMENTS_QUERY_KEY, ruleId, ruleBase],
          }),
          queryClient.invalidateQueries({
            queryKey: [HISTORY_QUERY_KEY, ruleId],
          }),
        ])
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('upload override document failed', err)
        message.error('Upload failed. Please try again.')
      } finally {
        pendingCountRef.current--
        setPlaceholders((prev) => prev.filter((p) => p.id !== placeholderId))
      }
    },
    [ruleId, ruleBase, validateFile, currentDocumentCount, queryClient]
  )

  return { upload, placeholders }
}
